import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { relancerCredit } from "../utilities/locationService";
import { messageServeur } from "../utilities/userService";
import { openSnackbar } from "../redux/actions";

// Crédit de Tips en échec : la décision (lieu approuvé, soumission validée,
// gagnant désigné) est enregistrée, mais user-service n'a pas confirmé le
// crédit. Badge rouge, et relance par un admin : location-service rejoue la
// même clé, user-service ne crédite jamais deux fois.

export type TypeCredit = "poi" | "soumission" | "challenge";

function dateCourte(d?: string | number | null): string {
  if (!d) return "";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "" : t.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function CreditEchecBadge({ message, date }: { message?: string | null; date?: string | number | null }) {
  const detail = [message, dateCourte(date)].filter(Boolean).join(" — ");
  return (
    <span
      title={detail || "user-service n'a pas confirmé le crédit"}
      style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: "100px", fontSize: "11px", fontWeight: 600, background: "rgba(162,58,13,0.12)", color: "#A23A0D", border: "1px solid rgba(162,58,13,0.25)", whiteSpace: "nowrap" }}
    >
      Crédit en échec
    </span>
  );
}

/**
 * Badge + bouton « Relancer le crédit » (bouton seul si `compact`). `peutRelancer` : droit d'écriture
 * de l'admin (un VIEWER voit le badge, pas le bouton). `onRelance` est appelé
 * après une relance confirmée, pour recharger l'objet.
 */
export default function RelanceCredit({ type, id, message, date, peutRelancer = true, compact = false, onRelance }: {
  type: TypeCredit;
  id: string;
  message?: string | null;
  date?: string | number | null;
  peutRelancer?: boolean;
  /** Bouton seul (le badge est affiché ailleurs, ex. colonne Statut). */
  compact?: boolean;
  onRelance?: (resultat: any) => void;
}) {
  const dispatch = useDispatch();
  const [enCours, setEnCours] = useState(false);

  const relancer = async () => {
    setEnCours(true);
    try {
      const r = await relancerCredit(type, id);
      const montant = Number(r?.montant) || 0;
      dispatch(openSnackbar(r?.dejaCredite
        ? "Crédit déjà enregistré par user-service : état mis à jour, rien n'a été crédité deux fois."
        : `Crédit confirmé : ${montant.toLocaleString("fr-FR")} Tips.`, "green"));
      onRelance?.(r);
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Relance du crédit impossible."), "red"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {!compact && <CreditEchecBadge message={message} date={date} />}
      {!compact && message && <span style={{ fontSize: 12, color: "#57534A" }}>{message}</span>}
      {peutRelancer && (
        <button
          type="button"
          onClick={relancer}
          disabled={enCours}
          style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid #A23A0D", background: "transparent", color: "#A23A0D", fontSize: 12, fontWeight: 600, cursor: enCours ? "default" : "pointer" }}
        >
          {enCours ? "Relance…" : "Relancer le crédit"}
        </button>
      )}
    </span>
  );
}
